import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { User, Mail, Send, Upload, FileSpreadsheet, X, Check, Download } from 'lucide-react';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../redux/blockingSlice';
import { sendInvite, sendInviteExcel, getAllInvites, reinvite, clearError, clearSuccess, checkInviteExcelFormat } from '../redux/ctgSlice';
import { checkEmailExists } from '../redux/authSlice';
import useAxiosAuth from '../hooks/useAxiosAuth';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';
import baseURL from '../utils/baseURL';

function ClickToGo() {
    const dispatch = useDispatch();
    const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
    const { isLoading, isUploading, isLoadingInvites, isReinviting, isDownloadingExcel, error, success, inviteResponse, excelResponse, invitations, total, page, limit, totalPages } = useSelector((state) => state.ctg);
    const { emailCheckLoading } = useSelector((state) => state.auth);
    const { t } = useTranslation(['clicktogo', 'common']);
    const axiosInstance = useAxiosAuth();
    const [activeTab, setActiveTab] = useState('Doctor');
    const [mobileTabOpen, setMobileTabOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // Store dispatch and axiosInstance in refs to avoid dependency issues
    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    // Update refs when values change
    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // Form states for Doctor tab
    const [doctorForm, setDoctorForm] = useState({
        username: '',
        email: ''
    });
    const [doctorErrors, setDoctorErrors] = useState({
        username: '',
        email: ''
    });
    const [doctorEmailStatus, setDoctorEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const doctorEmailCheckTimeoutRef = useRef(null);
    const lastCheckedDoctorEmailRef = useRef('');
    const isDoctorEmailCheckInProgressRef = useRef(false);

    // Form states for Patient tab
    const [patientForm, setPatientForm] = useState({
        username: '',
        email: ''
    });
    const [patientErrors, setPatientErrors] = useState({
        username: '',
        email: ''
    });
    const [patientEmailStatus, setPatientEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const patientEmailCheckTimeoutRef = useRef(null);
    const lastCheckedPatientEmailRef = useRef('');
    const isPatientEmailCheckInProgressRef = useRef(false);

    // Excel file states
    const [doctorExcelFile, setDoctorExcelFile] = useState(null);
    const [patientExcelFile, setPatientExcelFile] = useState(null);

    // Modal states
    const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
    const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
    const [doctorFileError, setDoctorFileError] = useState('');
    const [patientFileError, setPatientFileError] = useState('');

    // Excel format-check states (filled after backend /check-invite-excel-format)
    const [doctorFormatChecking, setDoctorFormatChecking] = useState(false);
    const [doctorFormatValid, setDoctorFormatValid] = useState(false);
    const [patientFormatChecking, setPatientFormatChecking] = useState(false);
    const [patientFormatValid, setPatientFormatValid] = useState(false);

    // Excel upload result modal state
    const [excelResultModal, setExcelResultModal] = useState({
        isOpen: false,
        type: 'success', // success, warning, error, info
        title: '',
        message: '',
        summary: null,
        details: [],
        userExistsDetails: [],
        duplicateDetails: [],
        originalDuplicateDetails: [], // Track original duplicates before reinvite
        reinvitedEmails: new Set(), // Track which emails were reinvited
        onConfirm: null,
        onCancel: null,
        showReinvite: false
    });

    // Tabs setup
    const tabs = [
        { key: 'Doctor', label: t('clicktogo.doctor_tab') },
        { key: 'Patient', label: t('clicktogo.patient_tab') }
    ];

    const handleTabClick = (e, key) => {
        if (resnavigate) {
            e.preventDefault();
            dispatch(setPendingNavigation({ kind: 'tab', value: key }));
            dispatch(tryingnavigate(true));
            return;
        }
        setActiveTab(key);
        setCurrentPage(1); // Reset to first page when switching tabs
    };

    // Auto-apply the requested tab once the dirty tab releases the block.
    useEffect(() => {
        if (!resnavigate && pendingNavigation?.kind === 'tab' && pendingNavigation.value) {
            setActiveTab(pendingNavigation.value);
            setCurrentPage(1);
            dispatch(clearPendingNavigation());
        }
    }, [resnavigate, pendingNavigation, dispatch]);

    // Email validation regex - matches DoctorRegister.jsx
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check for Doctor
    useEffect(() => {
        // Clear previous timeout
        if (doctorEmailCheckTimeoutRef.current) {
            clearTimeout(doctorEmailCheckTimeoutRef.current);
        }

        const email = doctorForm.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setDoctorEmailStatus(null);
            lastCheckedDoctorEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedDoctorEmailRef.current === email || isDoctorEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        doctorEmailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = doctorForm.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedDoctorEmailRef.current === trimmedEmail || isDoctorEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isDoctorEmailCheckInProgressRef.current = true;
            lastCheckedDoctorEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the email hasn't changed during the API call
                if (doctorForm.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email already exists (not available)
                        // exists: false means email doesn't exist (available)
                        setDoctorEmailStatus(!resultAction.payload.exists);
                    } else {
                        setDoctorEmailStatus(null);
                        lastCheckedDoctorEmailRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (doctorForm.email.trim() === trimmedEmail) {
                    setDoctorEmailStatus(null);
                    lastCheckedDoctorEmailRef.current = ''; // Reset on error
                }
            } finally {
                isDoctorEmailCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (doctorEmailCheckTimeoutRef.current) {
                clearTimeout(doctorEmailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorForm.email]);

    // Debounced email check for Patient
    useEffect(() => {
        // Clear previous timeout
        if (patientEmailCheckTimeoutRef.current) {
            clearTimeout(patientEmailCheckTimeoutRef.current);
        }

        const email = patientForm.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setPatientEmailStatus(null);
            lastCheckedPatientEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedPatientEmailRef.current === email || isPatientEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        patientEmailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = patientForm.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedPatientEmailRef.current === trimmedEmail || isPatientEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isPatientEmailCheckInProgressRef.current = true;
            lastCheckedPatientEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the email hasn't changed during the API call
                if (patientForm.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email already exists (not available)
                        // exists: false means email doesn't exist (available)
                        setPatientEmailStatus(!resultAction.payload.exists);
                    } else {
                        setPatientEmailStatus(null);
                        lastCheckedPatientEmailRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (patientForm.email.trim() === trimmedEmail) {
                    setPatientEmailStatus(null);
                    lastCheckedPatientEmailRef.current = ''; // Reset on error
                }
            } finally {
                isPatientEmailCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (patientEmailCheckTimeoutRef.current) {
                clearTimeout(patientEmailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientForm.email]);

    // Helper function to extract message string from object or string
    const getMessageString = (message) => {
        if (!message) return '';
        if (typeof message === 'string') return message;
        if (typeof message === 'object' && message.en) return message.en;
        if (typeof message === 'object' && message.fr) return message.fr;
        return String(message);
    };

    // Helper function to create scrollable email list HTML
    const createScrollableEmailList = (emails, title = 'Emails') => {
        if (!emails || emails.length === 0) return '';

        const emailList = Array.isArray(emails) ? emails : emails.split(', ');
        const emailItems = emailList.map((email, index) => `
            <div style="padding: 8px 12px; margin: 4px 0; background: #ffffff; border: 1px solid #e0e0e0; border-radius: 4px; border-left: 3px solid #4ABDBD; display: flex; align-items: center;">
                <span style="margin-right: 8px; color: #666; font-weight: 500;">${index + 1}.</span>
                <span style="color: #333; font-size: 14px; word-break: break-all;">${email.trim()}</span>
            </div>
        `).join('');

        return `
            <div style="margin: 15px 0;">
                <strong style="display: block; margin-bottom: 10px; color: #333; font-size: 15px;">${title}:</strong>
                <div style="max-height: 200px; overflow-y: auto; margin-top: 8px; padding: 4px; background: #f8f9fa; border-radius: 6px; border: 1px solid #ddd;">
                    ${emailItems}
                </div>
            </div>
        `;
    };

    // Helper function to create scrollable details table
    const createScrollableDetailsTable = (details, title = 'Details') => {
        if (!details || details.length === 0) return '';

        const tableRows = details.map(d => `
            <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 8px; text-align: left;">${d.name || '-'}</td>
                <td style="padding: 8px; text-align: left;">${d.email || '-'}</td>
                <td style="padding: 8px; text-align: left;">
                    <span style="padding: 2px 8px; background: ${d.status === 'User already exists' ? '#fff3cd' : '#d1ecf1'}; border-radius: 4px; font-size: 11px;">
                        ${d.status || '-'}
                    </span>
                </td>
            </tr>
        `).join('');

        return `
            <div style="margin: 10px 0;">
                <strong>${title}:</strong>
                <div style="max-height: 250px; overflow-y: auto; margin-top: 8px; border: 1px solid #ddd; border-radius: 6px;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                        <thead style="background: #f8f9fa; position: sticky; top: 0;">
                            <tr>
                                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">${t('clicktogo.name', 'Name')}</th>
                                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">${t('clicktogo.email', 'Email')}</th>
                                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">${t('clicktogo.status', 'Status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    };

    // Helper function to open Excel result modal
    const openExcelResultModal = (config) => {
        setExcelResultModal({
            isOpen: true,
            type: config.type || 'success',
            title: config.title || t('clicktogo.upload_results', 'Upload Results'),
            message: config.message || '',
            summary: config.summary || null,
            details: config.details || [],
            userExistsDetails: config.userExistsDetails || [],
            duplicateDetails: config.duplicateDetails || [],
            originalDuplicateDetails: config.originalDuplicateDetails || config.duplicateDetails || [],
            reinvitedEmails: config.reinvitedEmails || new Set(),
            onConfirm: config.onConfirm || null,
            onCancel: config.onCancel || null,
            showReinvite: config.showReinvite || false
        });
    };

    // Close Excel result modal
    const closeExcelResultModal = () => {
        setExcelResultModal({
            isOpen: false,
            type: 'success',
            title: '',
            message: '',
            summary: null,
            details: [],
            userExistsDetails: [],
            duplicateDetails: [],
            originalDuplicateDetails: [],
            reinvitedEmails: new Set(),
            onConfirm: null,
            onCancel: null,
            showReinvite: false
        });
    };

    // Handle input changes for Doctor tab
    const handleDoctorInputChange = (e) => {
        const { name, value } = e.target;

        // Restrict username to letters, spaces, hyphens, and apostrophes only, max 20 characters
        if (name === 'username') {
            // Prevent typing special characters and numbers
            if (value && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(value)) return;
            // Limit to 20 characters
            if (value.length > 20) return;
        }

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === 'email') {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (value && !/^[a-zA-Z0-9._+-@]*$/.test(value)) return;
            // Limit to 254 characters (RFC 5321 max length)
            if (value.length > 254) return;
        }

        setDoctorForm((prev) => ({ ...prev, [name]: value }));

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setDoctorEmailStatus(null);
            lastCheckedDoctorEmailRef.current = '';
            isDoctorEmailCheckInProgressRef.current = false;
        }

        // Validation
        if (name === 'username') {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                setDoctorErrors((prev) => ({ ...prev, username: t('clicktogo.username_required') }));
            } else if (trimmedValue.length < 2) {
                setDoctorErrors((prev) => ({ ...prev, username: t('clicktogo.username_min_length', 'Username must be at least 2 characters long') }));
            } else if (trimmedValue.length > 20) {
                setDoctorErrors((prev) => ({ ...prev, username: t('clicktogo.username_max_length', 'Username cannot exceed 20 characters') }));
            } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedValue)) {
                setDoctorErrors((prev) => ({ ...prev, username: t('clicktogo.username_invalid_chars', 'Username can only contain letters, spaces, hyphens, and apostrophes') }));
            } else if (/^\s+$/.test(value)) {
                setDoctorErrors((prev) => ({ ...prev, username: t('clicktogo.username_only_spaces', 'Username cannot contain only spaces') }));
            } else {
                setDoctorErrors((prev) => ({ ...prev, username: '' }));
            }
        }

        if (name === 'email') {
            if (!value.trim()) {
                setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.email_required') }));
            } else {
                const email = value.trim();
                // Check total length (RFC 5321: max 254 characters)
                if (email.length > 254) {
                    setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.email_too_long', 'Email address is too long (maximum 254 characters)') }));
                } else {
                    // Split email into local and domain parts
                    const parts = email.split('@');
                    if (parts.length !== 2) {
                        setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                    } else {
                        const [localPart, domainPart] = parts;
                        // Local part (before @) should be max 64 characters (RFC 5321)
                        if (localPart.length > 64) {
                            setDoctorErrors((prev) => ({ ...prev, email: t('validation.email_local_part_too_long', { ns: 'common' }) }));
                        } else if (localPart.length === 0) {
                            setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                        } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                            setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                        } else if (domainPart.length > 253) {
                            setDoctorErrors((prev) => ({ ...prev, email: t('validation.email_domain_part_too_long', { ns: 'common' }) }));
                        } else if (!emailRegex.test(email)) {
                            setDoctorErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format') }));
                        } else {
                            setDoctorErrors((prev) => ({ ...prev, email: '' }));
                        }
                    }
                }
            }
        }
    };

    // Handle input changes for Patient tab
    const handlePatientInputChange = (e) => {
        const { name, value } = e.target;

        // Restrict username to letters, spaces, hyphens, and apostrophes only, max 20 characters
        if (name === 'username') {
            // Prevent typing special characters and numbers
            if (value && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(value)) return;
            // Limit to 20 characters
            if (value.length > 20) return;
        }

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === 'email') {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (value && !/^[a-zA-Z0-9._+-@]*$/.test(value)) return;
            // Limit to 254 characters (RFC 5321 max length)
            if (value.length > 254) return;
        }

        setPatientForm((prev) => ({ ...prev, [name]: value }));

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setPatientEmailStatus(null);
            lastCheckedPatientEmailRef.current = '';
            isPatientEmailCheckInProgressRef.current = false;
        }

        // Validation
        if (name === 'username') {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                setPatientErrors((prev) => ({ ...prev, username: t('clicktogo.username_required') }));
            } else if (trimmedValue.length < 2) {
                setPatientErrors((prev) => ({ ...prev, username: t('clicktogo.username_min_length', 'Username must be at least 2 characters long') }));
            } else if (trimmedValue.length > 20) {
                setPatientErrors((prev) => ({ ...prev, username: t('clicktogo.username_max_length', 'Username cannot exceed 20 characters') }));
            } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedValue)) {
                setPatientErrors((prev) => ({ ...prev, username: t('clicktogo.username_invalid_chars', 'Username can only contain letters, spaces, hyphens, and apostrophes') }));
            } else if (/^\s+$/.test(value)) {
                setPatientErrors((prev) => ({ ...prev, username: t('clicktogo.username_only_spaces', 'Username cannot contain only spaces') }));
            } else {
                setPatientErrors((prev) => ({ ...prev, username: '' }));
            }
        }

        if (name === 'email') {
            if (!value.trim()) {
                setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.email_required') }));
            } else {
                const email = value.trim();
                // Check total length (RFC 5321: max 254 characters)
                if (email.length > 254) {
                    setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.email_too_long', 'Email address is too long (maximum 254 characters)') }));
                } else {
                    // Split email into local and domain parts
                    const parts = email.split('@');
                    if (parts.length !== 2) {
                        setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                    } else {
                        const [localPart, domainPart] = parts;
                        // Local part (before @) should be max 64 characters (RFC 5321)
                        if (localPart.length > 64) {
                            setPatientErrors((prev) => ({ ...prev, email: t('validation.email_local_part_too_long', { ns: 'common' }) }));
                        } else if (localPart.length === 0) {
                            setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                        } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                            setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format_msg', 'Invalid email format') }));
                        } else if (domainPart.length > 253) {
                            setPatientErrors((prev) => ({ ...prev, email: t('validation.email_domain_part_too_long', { ns: 'common' }) }));
                        } else if (!emailRegex.test(email)) {
                            setPatientErrors((prev) => ({ ...prev, email: t('clicktogo.invalid_email_format') }));
                        } else {
                            setPatientErrors((prev) => ({ ...prev, email: '' }));
                        }
                    }
                }
            }
        }
    };

    // Handle send button for Doctor
    const handleDoctorSend = async (e) => {
        e.preventDefault();

        // Validate
        let hasErrors = false;
        const newErrors = { username: '', email: '' };

        const trimmedUsername = doctorForm.username.trim();
        if (!trimmedUsername) {
            newErrors.username = t('clicktogo.username_required');
            hasErrors = true;
        } else if (trimmedUsername.length < 2) {
            newErrors.username = t('clicktogo.username_min_length', 'Username must be at least 2 characters long');
            hasErrors = true;
        } else if (trimmedUsername.length > 20) {
            newErrors.username = t('clicktogo.username_max_length', 'Username cannot exceed 20 characters');
            hasErrors = true;
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedUsername)) {
            newErrors.username = t('clicktogo.username_invalid_chars', 'Username can only contain letters, spaces, hyphens, and apostrophes');
            hasErrors = true;
        } else if (/^\s+$/.test(doctorForm.username)) {
            newErrors.username = t('clicktogo.username_only_spaces', 'Username cannot contain only spaces');
            hasErrors = true;
        }

        if (!doctorForm.email.trim()) {
            newErrors.email = t('clicktogo.email_required');
            hasErrors = true;
        } else {
            const email = doctorForm.email.trim();
            // Check total length (RFC 5321: max 254 characters)
            if (email.length > 254) {
                newErrors.email = t('clicktogo.email_too_long', 'Email address is too long (maximum 254 characters)');
                hasErrors = true;
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                    hasErrors = true;
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                        hasErrors = true;
                    } else if (localPart.length === 0) {
                        newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                        hasErrors = true;
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                        hasErrors = true;
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
                        hasErrors = true;
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('clicktogo.invalid_email_format');
                        hasErrors = true;
                    } else if (doctorEmailStatus === false) {
                        newErrors.email = t('clicktogo.email_already_invited', 'Email already Invited. You can reinvite this user.');
                        hasErrors = true;
                    } else if (doctorEmailStatus === null && !emailCheckLoading) {
                        newErrors.email = t('clicktogo.please_verify_email', 'Please verify your email address');
                        hasErrors = true;
                    }
                }
            }
        }

        if (hasErrors) {
            setDoctorErrors(newErrors);
            return;
        }

        // Ensure email is validated before sending
        if (doctorEmailStatus !== true) {
            toast.warn(t('clicktogo.verify_email_before_sending', 'Please verify that the email address is available before sending the invitation.'));
            return;
        }

        // SweetAlert confirmation
        const result = await Swal.fire({
            title: t('clicktogo.send_invitation_title'),
            text: t('clicktogo.send_invitation_text', { username: doctorForm.username, email: doctorForm.email }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('clicktogo.yes_send_invite'),
            cancelButtonText: t('clicktogo.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            // Prepare payload - trim all fields before sending
            const emailSend = [{
                name: doctorForm.username.trim(),
                email: doctorForm.email.trim()
            }];

            const response = await dispatch(sendInvite({
                emailSend,
                role: 'doctor'
            })).unwrap();

            // Check for userExists
            if (response.summary && response.summary.userExists > 0) {
                const userExistsDetails = response.summary.details?.filter(d => d.status === 'User already exists') || [];
                const userExistsEmails = userExistsDetails.map(d => d.email).join(', ');

                toast.warn(t('clicktogo.user_already_exists_in_system', 'The user with email {{emails}} already exists in the system.', { emails: userExistsEmails }));

                // Reset form
                setDoctorForm({ username: '', email: '' });
                setDoctorErrors({ username: '', email: '' });
                dispatch(clearError());
                dispatch(clearSuccess());

                // Refresh invitations list
                dispatch(getAllInvites({ role: 'doctor', page: currentPage, limit: itemsPerPage }));
                return;
            }

            // Check for duplicates
            if (response.summary && response.summary.successful === 0 && response.summary.duplicate > 0) {
                // Show duplicate message and ask for reinvite
                const duplicateDetails = response.summary.details || [];
                const duplicateEmails = duplicateDetails.map(d => d.email).join(', ');

                const result = await Swal.fire({
                    icon: 'warning',
                    title: t('clicktogo.email_already_invited_title', 'Email Already Invited'),
                    html: t('clicktogo.email_already_invited_html', 'The email <strong>{{emails}}</strong> already has an invitation. Do you want to reinvite?', { emails: duplicateEmails, interpolation: { escapeValue: false } }),
                    showCancelButton: true,
                    confirmButtonText: t('clicktogo.yes_re_invite'),
                    cancelButtonText: t('clicktogo.cancel'),
                    confirmButtonColor: '#4ABDBD',
                    cancelButtonColor: '#d33',
                });

                if (result.isConfirmed) {
                    // Call reinvite API
                    try {
                        const reinviteResponse = await dispatch(reinvite({
                            emailSend: duplicateDetails.map(d => ({ name: d.name, email: d.email })),
                            role: 'doctor'
                        })).unwrap();

                        toast.success(getMessageString(reinviteResponse.message) || t('clicktogo.reinvitation_sent_success', 'Reinvitation sent successfully'));

                        // Reset form after successful reinvite
                        setDoctorForm({ username: '', email: '' });
                        setDoctorErrors({ username: '', email: '' });
                        dispatch(clearError());
                        dispatch(clearSuccess());

                        // Refresh invitations list
                        dispatch(getAllInvites({ role: 'doctor', page: currentPage, limit: itemsPerPage }));
                    } catch (reinviteError) {
                        const errorMessage = reinviteError?.error?.message || reinviteError?.message || t('clicktogo.failed_to_reinvite', 'Failed to reinvite');
                        toast.warn(errorMessage);
                    }
                }
            } else {
                // Success
                toast.success(getMessageString(response.message) || t('clicktogo.invitation_sent_text', { username: doctorForm.username }));

                // Reset form after successful send
                setDoctorForm({ username: '', email: '' });
                setDoctorErrors({ username: '', email: '' });
                dispatch(clearError());
                dispatch(clearSuccess());

                // Refresh invitations list
                dispatch(getAllInvites({ role: 'doctor', page: currentPage, limit: itemsPerPage }));
            }
        } catch (error) {
            // Error handling
            const errorMessage = error?.error?.message || error?.message || t('clicktogo.failed_to_send', 'Failed to send invitation');
            toast.warn(errorMessage);
        }
    };

    // Handle send button for Patient
    const handlePatientSend = async (e) => {
        e.preventDefault();

        // Validate
        let hasErrors = false;
        const newErrors = { username: '', email: '' };

        const trimmedUsername = patientForm.username.trim();
        if (!trimmedUsername) {
            newErrors.username = t('clicktogo.username_required');
            hasErrors = true;
        } else if (trimmedUsername.length < 2) {
            newErrors.username = t('clicktogo.username_min_length', 'Username must be at least 2 characters long');
            hasErrors = true;
        } else if (trimmedUsername.length > 20) {
            newErrors.username = t('clicktogo.username_max_length', 'Username cannot exceed 20 characters');
            hasErrors = true;
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedUsername)) {
            newErrors.username = t('clicktogo.username_invalid_chars', 'Username can only contain letters, spaces, hyphens, and apostrophes');
            hasErrors = true;
        } else if (/^\s+$/.test(patientForm.username)) {
            newErrors.username = t('clicktogo.username_only_spaces', 'Username cannot contain only spaces');
            hasErrors = true;
        }

        if (!patientForm.email.trim()) {
            newErrors.email = t('clicktogo.email_required');
            hasErrors = true;
        } else {
            const email = patientForm.email.trim();
            // Check total length (RFC 5321: max 254 characters)
            if (email.length > 254) {
                newErrors.email = t('clicktogo.email_too_long', 'Email address is too long (maximum 254 characters)');
                hasErrors = true;
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                    hasErrors = true;
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                        hasErrors = true;
                    } else if (localPart.length === 0) {
                        newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                        hasErrors = true;
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('clicktogo.invalid_email_format_msg', 'Invalid email format');
                        hasErrors = true;
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
                        hasErrors = true;
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('clicktogo.invalid_email_format');
                        hasErrors = true;
                    } else if (patientEmailStatus === false) {
                        newErrors.email = t('clicktogo.email_already_invited', 'Email already Invited. You can reinvite this user.');
                        hasErrors = true;
                    } else if (patientEmailStatus === null && !emailCheckLoading) {
                        newErrors.email = t('clicktogo.please_verify_email', 'Please verify your email address');
                        hasErrors = true;
                    }
                }
            }
        }

        if (hasErrors) {
            setPatientErrors(newErrors);
            return;
        }

        // Ensure email is validated before sending
        if (patientEmailStatus !== true) {
            toast.warn(t('clicktogo.verify_email_before_sending', 'Please verify that the email address is available before sending the invitation.'));
            return;
        }

        // SweetAlert confirmation
        const result = await Swal.fire({
            title: t('clicktogo.send_invitation_title'),
            text: t('clicktogo.send_invitation_text', { username: patientForm.username, email: patientForm.email }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('clicktogo.yes_send_invite'),
            cancelButtonText: t('clicktogo.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            // Prepare payload - trim all fields before sending
            const emailSend = [{
                name: patientForm.username.trim(),
                email: patientForm.email.trim()
            }];

            const response = await dispatch(sendInvite({
                emailSend,
                role: 'patient'
            })).unwrap();

            // Check for userExists
            if (response.summary && response.summary.userExists > 0) {
                const userExistsDetails = response.summary.details?.filter(d => d.status === 'User already exists') || [];
                const userExistsEmails = userExistsDetails.map(d => d.email).join(', ');

                toast.warn(t('clicktogo.user_already_exists_in_system', 'The user with email {{emails}} already exists in the system.', { emails: userExistsEmails }));

                // Reset form
                setPatientForm({ username: '', email: '' });
                setPatientErrors({ username: '', email: '' });
                dispatch(clearError());
                dispatch(clearSuccess());

                // Refresh invitations list
                dispatch(getAllInvites({ role: 'patient', page: currentPage, limit: itemsPerPage }));
                return;
            }

            // Check for duplicates
            if (response.summary && response.summary.successful === 0 && response.summary.duplicate > 0) {
                // Show duplicate message and ask for reinvite
                const duplicateDetails = response.summary.details || [];
                const duplicateEmails = duplicateDetails.map(d => d.email).join(', ');

                const result = await Swal.fire({
                    icon: 'warning',
                    title: t('clicktogo.email_already_invited_title', 'Email Already Invited'),
                    html: t('clicktogo.email_already_invited_html', 'The email <strong>{{emails}}</strong> already has an invitation. Do you want to reinvite?', { emails: duplicateEmails, interpolation: { escapeValue: false } }),
                    showCancelButton: true,
                    confirmButtonText: t('clicktogo.yes_re_invite'),
                    cancelButtonText: t('clicktogo.cancel'),
                    confirmButtonColor: '#4ABDBD',
                    cancelButtonColor: '#d33',
                });

                if (result.isConfirmed) {
                    // Call reinvite API
                    try {
                        const reinviteResponse = await dispatch(reinvite({
                            emailSend: duplicateDetails.map(d => ({ name: d.name, email: d.email })),
                            role: 'patient'
                        })).unwrap();

                        toast.success(getMessageString(reinviteResponse.message) || t('clicktogo.reinvitation_sent_success', 'Reinvitation sent successfully'));

                        // Reset form after successful reinvite
                        setPatientForm({ username: '', email: '' });
                        setPatientErrors({ username: '', email: '' });
                        dispatch(clearError());
                        dispatch(clearSuccess());

                        // Refresh invitations list
                        dispatch(getAllInvites({ role: 'patient', page: currentPage, limit: itemsPerPage }));
                    } catch (reinviteError) {
                        const errorMessage = reinviteError?.error?.message || reinviteError?.message || t('clicktogo.failed_to_reinvite', 'Failed to reinvite');
                        toast.warn(errorMessage);
                    }
                }
            } else {
                // Success
                toast.success(getMessageString(response.message) || t('clicktogo.invitation_sent_text', { username: patientForm.username }));

                // Reset form after successful send
                setPatientForm({ username: '', email: '' });
                setPatientErrors({ username: '', email: '' });
                dispatch(clearError());
                dispatch(clearSuccess());

                // Refresh invitations list
                dispatch(getAllInvites({ role: 'patient', page: currentPage, limit: itemsPerPage }));
            }
        } catch (error) {
            // Error handling
            const errorMessage = error?.error?.message || error?.message || t('clicktogo.failed_to_send', 'Failed to send invitation');
            toast.warn(errorMessage);
        }
    };

    // Validate Excel file
    const validateExcelFile = (file) => {
        if (!file) {
            return t('clicktogo.please_select_file');
        }

        const validTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
        ];

        const validExtensions = ['.xlsx', '.xls', '.csv'];
        const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        const hasValidType = validTypes.includes(file.type);

        if (!hasValidType && !hasValidExtension) {
            return t('clicktogo.invalid_file_type');
        }

        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            return t('clicktogo.file_size_error');
        }

        return '';
    };

    // Validate the selected file against the backend format checker so the
    // user immediately sees whether the Excel layout matches what the bulk
    // invite endpoint expects.
    const runInviteFormatCheck = async (file, setFormatChecking, setFormatValid, setFileError) => {
        setFormatChecking(true);
        try {
            await dispatch(checkInviteExcelFormat(file)).unwrap();
            setFormatValid(true);
        } catch (err) {
            setFormatValid(false);
            setFileError(err?.error || t('clicktogo.invalid_file_format', 'Invalid Excel format'));
        } finally {
            setFormatChecking(false);
        }
    };

    // Handle Excel file change for Doctor
    const handleDoctorFileChange = async (e) => {
        const file = e.target.files[0];
        const error = validateExcelFile(file);

        setDoctorFormatValid(false);
        if (error) {
            setDoctorFileError(error);
            setDoctorExcelFile(null);
            return;
        }
        setDoctorFileError('');
        setDoctorExcelFile(file);
        await runInviteFormatCheck(file, setDoctorFormatChecking, setDoctorFormatValid, setDoctorFileError);
    };

    // Handle Excel file change for Patient
    const handlePatientFileChange = async (e) => {
        const file = e.target.files[0];
        const error = validateExcelFile(file);

        setPatientFormatValid(false);
        if (error) {
            setPatientFileError(error);
            setPatientExcelFile(null);
            return;
        }
        setPatientFileError('');
        setPatientExcelFile(file);
        await runInviteFormatCheck(file, setPatientFormatChecking, setPatientFormatValid, setPatientFileError);
    };

    // Open Doctor Excel modal
    const handleDoctorExcelClick = () => {
        setIsDoctorModalOpen(true);
    };

    // Open Patient Excel modal
    const handlePatientExcelClick = () => {
        setIsPatientModalOpen(true);
    };

    // Close Doctor modal
    const handleCloseDoctorModal = () => {
        setIsDoctorModalOpen(false);
        setDoctorExcelFile(null);
        setDoctorFileError('');
        setDoctorFormatValid(false);
        setDoctorFormatChecking(false);
    };

    // Close Patient modal
    const handleClosePatientModal = () => {
        setIsPatientModalOpen(false);
        setPatientExcelFile(null);
        setPatientFileError('');
        setPatientFormatValid(false);
        setPatientFormatChecking(false);
    };

    // Handle Sample Excel Download
    const handleSampleExcelDownload = async () => {
        try {
            const response = await axiosInstance.get(`${baseURL}/admin/download-sample-excel`, {
                responseType: 'blob',
            });
            const blob = response.data;

            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample-excel.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Show success message
            toast.success(t('clicktogo.sample_excel_downloaded') || 'Sample Excel file downloaded successfully');
        } catch (error) {
            toast.warn(error?.error || t('clicktogo.failed_to_download') || 'Failed to download sample Excel file');
        }
    };

    // Handle Download Excel from Modal Results
    const handleDownloadModalExcel = () => {
        try {
            const workbook = XLSX.utils.book_new();

            // Get reinvited emails set (convert to regular Set if it's already a Set)
            const reinvitedEmails = excelResultModal.reinvitedEmails instanceof Set
                ? excelResultModal.reinvitedEmails
                : new Set(excelResultModal.reinvitedEmails || []);

            // Use originalDuplicateDetails if available (when duplicates were reinvited)
            const duplicatesToProcess = excelResultModal.duplicateDetails.length > 0
                ? excelResultModal.duplicateDetails
                : (excelResultModal.originalDuplicateDetails || []);

            // Prepare data by status
            const sentData = [];
            const failedData = [];
            const userExistsData = [];
            const duplicateData = [];
            const otherData = [];

            let sentSN = 1, failedSN = 1, userExistsSN = 1, duplicateSN = 1, otherSN = 1;

            // Process details array if available
            if (excelResultModal.details && excelResultModal.details.length > 0) {
                excelResultModal.details.forEach((detail) => {
                    const status = (detail.status || '').toLowerCase();
                    const rowData = {
                        'S.No': 0, // Will be set based on category
                        'Name': detail.name || '-',
                        'Email': detail.email || '-',
                        'Role': detail.role || '-',
                        'Status': detail.status || '-'
                    };

                    if (status === 'sent') {
                        rowData['S.No'] = sentSN++;
                        sentData.push(rowData);
                    } else if (status === 'failed') {
                        rowData['S.No'] = failedSN++;
                        failedData.push(rowData);
                    } else if (status.includes('user already exists')) {
                        rowData['S.No'] = userExistsSN++;
                        userExistsData.push(rowData);
                    } else if (status.includes('invitation already exists') || status.includes('duplicate')) {
                        rowData['S.No'] = duplicateSN++;
                        // Check if this email was reinvited
                        rowData['Re-Invited'] = reinvitedEmails.has(detail.email) ? 'Re-Invited' : 'Not Re-Invited';
                        duplicateData.push(rowData);
                    } else if (status.includes('success')) {
                        rowData['S.No'] = sentSN++;
                        sentData.push(rowData);
                    } else {
                        rowData['S.No'] = otherSN++;
                        otherData.push(rowData);
                    }
                });
            } else {
                // Process userExistsDetails
                if (excelResultModal.userExistsDetails && excelResultModal.userExistsDetails.length > 0) {
                    excelResultModal.userExistsDetails.forEach((detail) => {
                        userExistsData.push({
                            'S.No': userExistsSN++,
                            'Name': detail.name || '-',
                            'Email': detail.email || '-',
                            'Role': detail.role || '-',
                            'Status': detail.status || t('clicktogo.user_already_exists_status', 'User already exists')
                        });
                    });
                }

                // Process duplicateDetails or originalDuplicateDetails
                if (duplicatesToProcess.length > 0) {
                    duplicatesToProcess.forEach((detail) => {
                        duplicateData.push({
                            'S.No': duplicateSN++,
                            'Name': detail.name || '-',
                            'Email': detail.email || '-',
                            'Role': detail.role || '-',
                            'Status': detail.status || t('clicktogo.invitation_already_exists_status', 'Invitation already exists'),
                            'Re-Invited': reinvitedEmails.has(detail.email) ? 'Re-Invited' : 'Not Re-Invited'
                        });
                    });
                }
            }

            // Create a single worksheet with all tables
            const allRows = [];
            const styleMap = []; // Track styles: [{row, col, style}, ...]
            let currentRow = 0;

            // Helper to add styled row
            const addStyledRow = (data, rowNum, styleFn) => {
                allRows.push(data);
                if (styleFn) {
                    data.forEach((cell, col) => {
                        if (cell !== undefined && cell !== null) {
                            const style = styleFn(rowNum, col, cell);
                            if (style) {
                                styleMap.push({ row: rowNum, col, style });
                            }
                        }
                    });
                }
            };

            // Sent Invitations Table
            if (sentData.length > 0) {
                addStyledRow([t('clicktogo.sent_invitations', 'Sent Invitations')], currentRow, () => ({ font: { bold: true, sz: 14, color: { rgb: 'FF2E7D32' } }, fill: { fgColor: { rgb: 'FFE8F5E9' } } }));
                currentRow++;
                allRows.push([]); // Empty row
                currentRow++;
                // Headers
                const headers = Object.keys(sentData[0]);
                addStyledRow(headers, currentRow, (r, c) => ({ font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF4ABDBD' } }, alignment: { horizontal: 'center' } }));
                currentRow++;
                // Data rows
                sentData.forEach((row) => {
                    const rowData = headers.map(h => row[h]);
                    addStyledRow(rowData, currentRow, (r, c) => {
                        if (headers[c] === 'Status') {
                            return { fill: { fgColor: { rgb: 'FFC8E6C9' } }, font: { color: { rgb: 'FF2E7D32' }, bold: true } };
                        }
                        return null;
                    });
                    currentRow++;
                });
                allRows.push([]); // Empty row between tables
                currentRow++;
            }

            // Failed Invitations Table
            if (failedData.length > 0) {
                addStyledRow([t('clicktogo.failed_invitations', 'Failed Invitations')], currentRow, () => ({ font: { bold: true, sz: 14, color: { rgb: 'FFC62828' } }, fill: { fgColor: { rgb: 'FFFFEBEE' } } }));
                currentRow++;
                allRows.push([]); // Empty row
                currentRow++;
                const headers = Object.keys(failedData[0]);
                addStyledRow(headers, currentRow, (r, c) => ({ font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FFD32F2F' } }, alignment: { horizontal: 'center' } }));
                currentRow++;
                failedData.forEach((row) => {
                    const rowData = headers.map(h => row[h]);
                    addStyledRow(rowData, currentRow, (r, c) => {
                        // Style entire row with light red
                        if (headers[c] === 'Status') {
                            return { fill: { fgColor: { rgb: 'FFEF5350' } }, font: { color: { rgb: 'FFFFFFFF' }, bold: true } };
                        }
                        return { fill: { fgColor: { rgb: 'FFFFCDD2' } } };
                    });
                    currentRow++;
                });
                allRows.push([]); // Empty row between tables
                currentRow++;
            }

            // User Already Exists Table
            if (userExistsData.length > 0) {
                addStyledRow([t('clicktogo.users_already_exist', 'Users Already Exist')], currentRow, () => ({ font: { bold: true, sz: 14, color: { rgb: 'FFF57C00' } }, fill: { fgColor: { rgb: 'FFFFF3E0' } } }));
                currentRow++;
                allRows.push([]); // Empty row
                currentRow++;
                const headers = Object.keys(userExistsData[0]);
                addStyledRow(headers, currentRow, (r, c) => ({ font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FFFFB74D' } }, alignment: { horizontal: 'center' } }));
                currentRow++;
                userExistsData.forEach((row) => {
                    const rowData = headers.map(h => row[h]);
                    addStyledRow(rowData, currentRow, (r, c) => {
                        if (headers[c] === 'Status') {
                            return { fill: { fgColor: { rgb: 'FFFFF9C4' } }, font: { color: { rgb: 'FFF57C00' }, bold: true } };
                        }
                        return null;
                    });
                    currentRow++;
                });
                allRows.push([]); // Empty row between tables
                currentRow++;
            }

            // Duplicate Invitations Table (with Re-Invited column)
            if (duplicateData.length > 0) {
                addStyledRow([t('clicktogo.duplicate_invitations', 'Duplicate Invitations')], currentRow, () => ({ font: { bold: true, sz: 14, color: { rgb: 'FF1976D2' } }, fill: { fgColor: { rgb: 'FFE3F2FD' } } }));
                currentRow++;
                allRows.push([]); // Empty row
                currentRow++;
                const headers = Object.keys(duplicateData[0]);
                addStyledRow(headers, currentRow, (r, c) => ({ font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF42A5F5' } }, alignment: { horizontal: 'center' } }));
                currentRow++;
                duplicateData.forEach((row) => {
                    const rowData = headers.map(h => row[h] || '-');
                    addStyledRow(rowData, currentRow, (r, c) => {
                        if (headers[c] === 'Status') {
                            return { fill: { fgColor: { rgb: 'FFBBDEFB' } }, font: { color: { rgb: 'FF1976D2' }, bold: true } };
                        }
                        return null;
                    });
                    currentRow++;
                });
                allRows.push([]); // Empty row between tables
                currentRow++;
            }

            // Other Status Table
            if (otherData.length > 0) {
                addStyledRow([t('clicktogo.other_status', 'Other Status')], currentRow, () => ({ font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: 'FFF5F5F5' } } }));
                currentRow++;
                allRows.push([]); // Empty row
                currentRow++;
                const headers = Object.keys(otherData[0]);
                addStyledRow(headers, currentRow, (r, c) => ({ font: { bold: true }, fill: { fgColor: { rgb: 'FFE0E0E0' } }, alignment: { horizontal: 'center' } }));
                currentRow++;
                otherData.forEach((row) => {
                    allRows.push(headers.map(h => row[h]));
                    currentRow++;
                });
            }

            // If no data, show error
            if (allRows.length === 0) {
                toast.warn(t('clicktogo.no_data_to_download', 'There is no data to download'));
                return;
            }

            // Create worksheet from all rows
            const worksheet = XLSX.utils.aoa_to_sheet(allRows);

            // Apply styles from styleMap
            styleMap.forEach(({ row, col, style }) => {
                const cell = XLSX.utils.encode_cell({ r: row, c: col });
                if (worksheet[cell]) {
                    if (!worksheet[cell].s) worksheet[cell].s = {};
                    Object.assign(worksheet[cell].s, style);
                }
            });

            // Style headers and titles (fallback for any missed cells)
            const range = XLSX.utils.decode_range(worksheet['!ref']);

            // Style title rows (bold, larger font) - apply to entire title row
            for (let row = 0; row <= range.e.r; row++) {
                const cellA = XLSX.utils.encode_cell({ r: row, c: 0 });
                if (worksheet[cellA] && typeof worksheet[cellA].v === 'string' &&
                    (worksheet[cellA].v.includes('Sent') ||
                        worksheet[cellA].v.includes('Failed') ||
                        worksheet[cellA].v.includes('Users Already') ||
                        worksheet[cellA].v.includes('Duplicate') ||
                        worksheet[cellA].v.includes('Other Status'))) {
                    if (!worksheet[cellA].s) worksheet[cellA].s = {};
                    if (!worksheet[cellA].s.font) worksheet[cellA].s.font = {};
                    worksheet[cellA].s.font.bold = true;
                    worksheet[cellA].s.font.sz = 14;
                }
            }

            // Style header rows (fallback)
            for (let row = 0; row <= range.e.r; row++) {
                const cellA = XLSX.utils.encode_cell({ r: row, c: 0 });
                if (worksheet[cellA] && worksheet[cellA].v === 'S.No') {
                    // This is a header row, ensure all cells in this row are styled
                    for (let col = 0; col <= range.e.c; col++) {
                        const cell = XLSX.utils.encode_cell({ r: row, c: col });
                        if (worksheet[cell] && !worksheet[cell].s) {
                            worksheet[cell].s = { font: { bold: true }, fill: { fgColor: { rgb: 'FFE0E0E0' } } };
                        }
                    }
                }
            }

            // Set column widths
            const columnWidths = [
                { wch: 8 },  // S.No
                { wch: 20 }, // Name
                { wch: 35 }, // Email
                { wch: 12 }, // Role
                { wch: 25 }, // Status
                { wch: 15 }  // Re-Invited
            ];
            worksheet['!cols'] = columnWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, t('clicktogo.upload_results', 'Upload Results'));

            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `upload-results-${timestamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Show success message
            toast.success(t('clicktogo.excel_downloaded_success', 'Excel file downloaded successfully'));
        } catch (error) {
            toast.warn(t('clicktogo.failed_to_generate_excel', 'Failed to generate Excel file'));
        }
    };

    // Handle Doctor Excel upload submit
    const handleDoctorExcelSubmit = async () => {
        const error = validateExcelFile(doctorExcelFile);

        if (error) {
            setDoctorFileError(error);
            return;
        }
        if (!doctorFormatValid) {
            setDoctorFileError(t('clicktogo.fix_format_first', 'Please fix the file format errors before uploading.'));
            return;
        }

        // SweetAlert confirmation
        const result = await Swal.fire({
            title: t('clicktogo.upload_excel_title'),
            text: t('clicktogo.upload_excel_text', { filename: doctorExcelFile.name, role: t('clicktogo.doctor_tab').toLowerCase() }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('clicktogo.yes_upload'),
            cancelButtonText: t('clicktogo.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await dispatch(sendInviteExcel({
                file: doctorExcelFile,
                role: 'doctor'
            })).unwrap();

            // Process all statuses together
            const summary = response.summary || {};
            const details = response.summary?.details || [];

            // Separate details by status
            const userExistsDetails = details.filter(d => d.status === 'User already exists');
            const duplicateDetails = details.filter(d => d.status === 'Invitation already exists');
            const successfulCount = summary.successful || 0;
            const failedCount = summary.failed || 0;
            const duplicateCount = summary.duplicate || 0;
            const userExistsCount = summary.userExists || 0;

            const message = getMessageString(response.message) || t('clicktogo.upload_successful');

            // Determine modal type
            let modalType = 'success';
            if (userExistsCount > 0 || duplicateCount > 0) {
                modalType = 'warning';
            }
            if (failedCount > 0 && successfulCount === 0) {
                modalType = 'error';
            }

            // Show custom modal
            openExcelResultModal({
                type: modalType,
                title: t('clicktogo.upload_results', 'Upload Results'),
                message: message,
                summary: summary,
                details: details,
                userExistsDetails: userExistsDetails,
                duplicateDetails: duplicateDetails,
                showReinvite: duplicateCount > 0,
                onConfirm: duplicateCount > 0 ? async () => {
                    if (duplicateDetails.length > 0) {
                        try {
                            const reinviteResponse = await dispatch(reinvite({
                                emailSend: duplicateDetails.map(d => ({ name: d.name, email: d.email })),
                                role: 'doctor'
                            })).unwrap();

                            const reinviteMessage = getMessageString(reinviteResponse.message) || t('clicktogo.reinvitations_sent_success', 'Reinvitations sent successfully');

                            // Track reinvited emails
                            const reinvitedEmailsSet = new Set(duplicateDetails.map(d => d.email));

                            // Show success modal
                            openExcelResultModal({
                                type: 'success',
                                title: t('clicktogo.upload_complete', 'Upload Complete'),
                                message: `${message}\n${reinviteMessage}`,
                                summary: summary,
                                details: details,
                                userExistsDetails: userExistsDetails,
                                duplicateDetails: [],
                                originalDuplicateDetails: duplicateDetails, // Preserve original duplicates
                                reinvitedEmails: reinvitedEmailsSet, // Track reinvited emails
                                showReinvite: false,
                                onCancel: () => {
                                    closeExcelResultModal();
                                    handleCloseDoctorModal();
                                    dispatch(clearError());
                                    dispatch(clearSuccess());
                                    dispatch(getAllInvites({ role: 'doctor', page: currentPage, limit: itemsPerPage }));
                                }
                            });
                        } catch (reinviteError) {
                            const errorMessage = reinviteError?.error?.message || reinviteError?.message || t('clicktogo.failed_to_reinvite', 'Failed to reinvite');
                            toast.warn(errorMessage);
                        }
                    }
                } : null,
                onCancel: () => {
                    closeExcelResultModal();
                    handleCloseDoctorModal();
                    dispatch(clearError());
                    dispatch(clearSuccess());
                    dispatch(getAllInvites({ role: 'doctor' }));
                }
            });
        } catch (error) {
            // Error handling
            const errorMessage = error?.error?.message || error?.message || t('clicktogo.failed_to_upload');
            toast.warn(errorMessage);
        }
    };

    // Handle Patient Excel upload submit
    const handlePatientExcelSubmit = async () => {
        const error = validateExcelFile(patientExcelFile);

        if (error) {
            setPatientFileError(error);
            return;
        }
        if (!patientFormatValid) {
            setPatientFileError(t('clicktogo.fix_format_first', 'Please fix the file format errors before uploading.'));
            return;
        }

        // SweetAlert confirmation
        const result = await Swal.fire({
            title: t('clicktogo.upload_excel_title'),
            text: t('clicktogo.upload_excel_text', { filename: patientExcelFile.name, role: t('clicktogo.patient_tab').toLowerCase() }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('clicktogo.yes_upload'),
            cancelButtonText: t('clicktogo.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await dispatch(sendInviteExcel({
                file: patientExcelFile,
                role: 'patient'
            })).unwrap();

            // Process all statuses together
            const summary = response.summary || {};
            const details = response.summary?.details || [];

            // Separate details by status
            const userExistsDetails = details.filter(d => d.status === 'User already exists');
            const duplicateDetails = details.filter(d => d.status === 'Invitation already exists');
            const successfulCount = summary.successful || 0;
            const failedCount = summary.failed || 0;
            const duplicateCount = summary.duplicate || 0;
            const userExistsCount = summary.userExists || 0;

            const message = getMessageString(response.message) || t('clicktogo.upload_successful');

            // Determine modal type
            let modalType = 'success';
            if (userExistsCount > 0 || duplicateCount > 0) {
                modalType = 'warning';
            }
            if (failedCount > 0 && successfulCount === 0) {
                modalType = 'error';
            }

            // Show custom modal
            openExcelResultModal({
                type: modalType,
                title: t('clicktogo.upload_results', 'Upload Results'),
                message: message,
                summary: summary,
                details: details,
                userExistsDetails: userExistsDetails,
                duplicateDetails: duplicateDetails,
                showReinvite: duplicateCount > 0,
                onConfirm: duplicateCount > 0 ? async () => {
                    if (duplicateDetails.length > 0) {
                        try {
                            const reinviteResponse = await dispatch(reinvite({
                                emailSend: duplicateDetails.map(d => ({ name: d.name, email: d.email })),
                                role: 'patient'
                            })).unwrap();

                            const reinviteMessage = getMessageString(reinviteResponse.message) || t('clicktogo.reinvitations_sent_success', 'Reinvitations sent successfully');

                            // Track reinvited emails
                            const reinvitedEmailsSet = new Set(duplicateDetails.map(d => d.email));

                            // Show success modal
                            openExcelResultModal({
                                type: 'success',
                                title: t('clicktogo.upload_complete', 'Upload Complete'),
                                message: `${message}\n${reinviteMessage}`,
                                summary: summary,
                                details: details,
                                userExistsDetails: userExistsDetails,
                                duplicateDetails: [],
                                originalDuplicateDetails: duplicateDetails, // Preserve original duplicates
                                reinvitedEmails: reinvitedEmailsSet, // Track reinvited emails
                                showReinvite: false,
                                onCancel: () => {
                                    closeExcelResultModal();
                                    handleClosePatientModal();
                                    dispatch(clearError());
                                    dispatch(clearSuccess());
                                    dispatch(getAllInvites({ role: 'patient', page: currentPage, limit: itemsPerPage }));
                                }
                            });
                        } catch (reinviteError) {
                            const errorMessage = reinviteError?.error?.message || reinviteError?.message || t('clicktogo.failed_to_reinvite', 'Failed to reinvite');
                            toast.warn(errorMessage);
                        }
                    }
                } : null,
                onCancel: () => {
                    closeExcelResultModal();
                    handleClosePatientModal();
                    dispatch(clearError());
                    dispatch(clearSuccess());
                    dispatch(getAllInvites({ role: 'patient' }));
                }
            });
        } catch (error) {
            // Error handling
            const errorMessage = error?.error?.message || error?.message || t('clicktogo.failed_to_upload');
            toast.warn(errorMessage);
        }
    };

    // Fetch invitations when component mounts or activeTab changes
    useEffect(() => {
        const role = activeTab.toLowerCase(); // 'doctor' or 'patient'
        dispatch(getAllInvites({ role, page: currentPage, limit: itemsPerPage }));
    }, [activeTab, currentPage, itemsPerPage, dispatch]);

    // Handle page change
    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    // Handle reinvite for a single invitation
    const handleReinvite = async (invitation) => {
        const result = await Swal.fire({
            title: t('clicktogo.re_invite_confirmation_title'),
            text: t('clicktogo.re_invite_confirmation_text', { email: invitation.email, name: invitation.name }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('clicktogo.yes_re_invite'),
            cancelButtonText: t('clicktogo.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            const emailSend = [{
                name: invitation.name,
                email: invitation.email
            }];

            const reinviteResponse = await dispatch(reinvite({
                emailSend,
                role: activeTab.toLowerCase()
            })).unwrap();

            toast.success(getMessageString(reinviteResponse.message) || t('clicktogo.reinvitation_sent_text'));

            // Refresh invitations list
            dispatch(getAllInvites({ role: activeTab.toLowerCase(), page: currentPage, limit: itemsPerPage }));
            dispatch(clearError());
            dispatch(clearSuccess());
        } catch (error) {
            const errorMessage = error?.error?.message || error?.message || t('clicktogo.failed_to_reinvite', 'Failed to reinvite');
            toast.warn(errorMessage);
        }
    };

    // Close mobile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="clicktogo-mobile-tabs-dropdown"]')) {
                setMobileTabOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileTabOpen]);

    return (
        <div data-testid="clicktogo-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            {/* Heading */}
            <div data-testid="clicktogo-header" className="mb-6">
                <h1 data-testid="clicktogo-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('clicktogo.title')}
                </h1>
            </div>

            {/* Modern Navigation Tabs */}
            <div data-testid="clicktogo-tabs-container" className="mb-6">
                {/* Desktop Navigation Tabs */}
                <div data-testid="clicktogo-tabs" className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 relative">
                    {tabs.map(({ key, label }) => (
                        <button
                            key={key}
                            data-testid={`clicktogo-tab-${key}`}
                            onClick={(e) => handleTabClick(e, key)}
                            className={`group relative px-6 py-3 text-sm font-semibold transition-all duration-300 rounded-xl z-10
                                ${activeTab === key
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                        >
                            {activeTab === key && (
                                <motion.div
                                    layoutId="activeCtgTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Mobile Navigation - Dropdown Style */}
                <div data-testid="clicktogo-mobile-tabs-dropdown" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2">
                        <div className="relative">
                            <button
                                data-testid="mobile-tab-selector"
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            >
                                <span>{tabs.find(tab => tab.key === activeTab)?.label}</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                                    {tabs.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            data-testid={`mobile-clicktogo-tab-${key}`}
                                            onClick={(e) => {
                                                handleTabClick(e, key);
                                                setMobileTabOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0
                                                ${activeTab === key
                                                    ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                    : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'
                                                } cursor-pointer`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                <div data-testid="clicktogo-content" className="p-4 pb-0 mt-6">
                    {/* Doctor Tab Content */}
                    {activeTab === 'Doctor' && (
                        <div data-testid="doctor-tab-content" className="space-y-4">
                            <form data-testid="ctg-doctor-form" onSubmit={handleDoctorSend} className="space-y-3">
                                {/* Single Row: Username, Email, Send Button, Upload Excel */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {/* Username Field */}
                                    <div className="flex-1 w-full sm:w-auto flex flex-col">
                                        <label data-testid="ctg-doctor-username-label" htmlFor="doctor-username" className="block text-sm font-semibold text-gray-700 mb-2 h-6">
                                            {t('clicktogo.username_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className={`relative flex items-center border rounded-xl px-3 py-2.5 bg-white transition-all duration-300 h-[42px] ${doctorErrors.username
                                            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
                                            : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD] hover:border-[#4ABDBD]'
                                            }`}>
                                            <User className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                            <input
                                                data-testid="ctg-doctor-username-input"
                                                id="doctor-username"
                                                name="username"
                                                type="text"
                                                value={doctorForm.username}
                                                onChange={handleDoctorInputChange}
                                                placeholder={t('clicktogo.username_placeholder')}
                                                maxLength={20}
                                                className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                            />
                                        </div>
                                        <div className="min-h-[20px] mt-1">
                                            {doctorErrors.username && (
                                                <p data-testid="ctg-doctor-username-error" className="text-red-500 text-xs">{doctorErrors.username}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Email Field */}
                                    <div className="flex-1 w-full sm:w-auto flex flex-col">
                                        <label data-testid="ctg-doctor-email-label" htmlFor="doctor-email" className="block text-sm font-semibold text-gray-700 mb-2 h-6">
                                            {t('clicktogo.email_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className={`relative flex items-center border rounded-xl px-3 py-2.5 bg-white transition-all duration-300 h-[42px] ${doctorErrors.email
                                            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
                                            : doctorEmailStatus === false
                                                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                                : doctorEmailStatus === true
                                                    ? 'border-green-300 focus-within:ring-2 focus-within:ring-green-500'
                                                    : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD] hover:border-[#4ABDBD]'
                                            }`}>
                                            <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                            <input
                                                data-testid="ctg-doctor-email-input"
                                                id="doctor-email"
                                                name="email"
                                                type="email"
                                                value={doctorForm.email}
                                                onChange={handleDoctorInputChange}
                                                placeholder={t('clicktogo.email_placeholder')}
                                                maxLength={254}
                                                className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm pr-8"
                                            />
                                            {emailCheckLoading && (
                                                <span data-testid="ctg-doctor-email-checking-spinner" className="absolute right-3 w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                            )}
                                            {!emailCheckLoading && doctorEmailStatus === true && (
                                                <Check data-testid="ctg-doctor-email-available-icon" className="absolute right-3 text-green-500 w-5 h-5" />
                                            )}
                                            {!emailCheckLoading && doctorEmailStatus === false && (
                                                <X data-testid="ctg-doctor-email-taken-icon" className="absolute right-3 text-red-500 w-5 h-5" />
                                            )}
                                        </div>
                                        <div className="min-h-[20px] mt-1">
                                            {doctorErrors.email && (
                                                <p data-testid="ctg-doctor-email-error" className="text-red-500 text-xs">{doctorErrors.email}</p>
                                            )}
                                            {!emailCheckLoading && doctorEmailStatus === false && !doctorErrors.email && (
                                                <p data-testid="ctg-doctor-email-already-invited-msg" className="text-red-500 text-xs">{t('clicktogo.email_already_invited', 'Email already Invited. You can reinvite this user.')}</p>
                                            )}
                                        </div>
                                        {/* {doctorEmailStatus === true && !doctorErrors.email && (
                                            <p className="text-green-600 text-xs mt-1">Email is available</p>
                                        )} */}
                                    </div>

                                    {/* Send Button */}
                                    <div className="w-full sm:w-auto flex flex-col">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 h-6 opacity-0">
                                            {t('clicktogo.action_label')}
                                        </label>
                                        <button
                                            data-testid="ctg-doctor-send-button"
                                            type="submit"
                                            disabled={isLoading}
                                            className={`flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto h-[42px] ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span data-testid="ctg-doctor-send-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                                    {t('clicktogo.sending')}
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4" />
                                                    {t('clicktogo.send_button')}
                                                </>
                                            )}
                                        </button>
                                        <div className="min-h-[20px]"></div>
                                    </div>

                                    {/* Upload Excel Button */}
                                    <div className="w-full sm:w-auto flex flex-col">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 h-6 opacity-0">
                                            {t('clicktogo.action_label')}
                                        </label>
                                        <button
                                            data-testid="ctg-doctor-upload-excel-button"
                                            type="button"
                                            onClick={handleDoctorExcelClick}
                                            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto h-[42px]"
                                        >
                                            <FileSpreadsheet className="w-4 h-4" />
                                            {t('clicktogo.upload_excel')}
                                        </button>
                                        <div className="min-h-[20px]"></div>
                                    </div>
                                </div>
                            </form>

                            {/* Invitations Table */}
                            <div data-testid="ctg-doctor-invitations-table-container" className="mt-6 overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="ctg-doctor-invitations-table" className="w-full border-collapse rounded-md overflow-hidden">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                            <th data-testid="ctg-doctor-table-header-sno" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.s_no')}</th>
                                            <th data-testid="ctg-doctor-table-header-name" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.name')}</th>
                                            <th data-testid="ctg-doctor-table-header-email" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.email')}</th>
                                            <th data-testid="ctg-doctor-table-header-invited-by" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.invited_by')}</th>
                                            <th data-testid="ctg-doctor-table-header-action" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('clicktogo.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoadingInvites ? (
                                            <tr data-testid="ctg-doctor-invitations-loading-row">
                                                <td colSpan="5" className="p-4 text-center text-gray-500">
                                                    <span data-testid="ctg-doctor-invitations-loading-spinner" className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                                    <span data-testid="ctg-doctor-invitations-loading-text" className="ml-2">{t('clicktogo.loading')}</span>
                                                </td>
                                            </tr>
                                        ) : invitations && invitations.length > 0 ? (
                                            invitations.map((invitation, index) => {
                                                const serialNumber = ((currentPage || 1) - 1) * (itemsPerPage || 5) + index + 1;
                                                return (
                                                    <tr key={invitation._id} data-testid={`ctg-doctor-invitation-row-${invitation._id}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                        <td data-testid={`ctg-doctor-invitation-sno-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{serialNumber}</td>
                                                        <td data-testid={`ctg-doctor-invitation-name-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.name || '-'}</td>
                                                        <td data-testid={`ctg-doctor-invitation-email-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.email || '-'}</td>
                                                        <td data-testid={`ctg-doctor-invitation-invited-by-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.adminName || '-'}</td>
                                                        <td className="p-3 text-xs text-center">
                                                            {invitation.isUsed ? (
                                                                <span data-testid={`ctg-doctor-invitation-accepted-${invitation._id}`} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-green-500/10 to-green-300/10 text-green-700 border border-green-500/20">
                                                                    {t('clicktogo.accepted')}
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    data-testid={`ctg-doctor-invitation-reinvite-button-${invitation._id}`}
                                                                    type="button"
                                                                    onClick={() => handleReinvite(invitation)}
                                                                    disabled={isReinviting}
                                                                    className={`px-4 py-1.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white text-xs font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer ${isReinviting ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                >
                                                                    {isReinviting ? t('clicktogo.sending_reinvite') : t('clicktogo.re_invite')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr data-testid="ctg-doctor-invitations-empty-row">
                                                <td colSpan="5" className="p-4 text-center text-gray-500">
                                                    {t('clicktogo.no_invitations_found', 'No invitations found')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {!isLoadingInvites && total > 0 && (
                                <Pagination
                                    data-testid="ctg-doctor-pagination"
                                    totalItems={total}
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    custombutton={invitations && invitations.length === 0 ? 'disable' : ''}
                                />
                            )}
                        </div>
                    )}

                    {/* Patient Tab Content */}
                    {activeTab === 'Patient' && (
                        <div data-testid="patient-tab-content" className="space-y-4">
                            <form data-testid="ctg-patient-form" onSubmit={handlePatientSend} className="space-y-3">
                                {/* Single Row: Username, Email, Send Button, Upload Excel */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {/* Username Field */}
                                    <div className="flex-1 w-full sm:w-auto flex flex-col">
                                        <label data-testid="ctg-patient-username-label" htmlFor="patient-username" className="block text-sm font-semibold text-gray-700 mb-2 h-6">
                                            {t('clicktogo.username_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className={`relative flex items-center border rounded-xl px-3 py-2.5 bg-white transition-all duration-300 h-[42px] ${patientErrors.username
                                            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
                                            : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD] hover:border-[#4ABDBD]'
                                            }`}>
                                            <User className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                            <input
                                                data-testid="ctg-patient-username-input"
                                                id="patient-username"
                                                name="username"
                                                type="text"
                                                value={patientForm.username}
                                                onChange={handlePatientInputChange}
                                                placeholder={t('clicktogo.username_placeholder')}
                                                maxLength={20}
                                                className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                            />
                                        </div>
                                        <div className="min-h-[20px] mt-1">
                                            {patientErrors.username && (
                                                <p data-testid="ctg-patient-username-error" className="text-red-500 text-xs">{patientErrors.username}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Email Field */}
                                    <div className="flex-1 w-full sm:w-auto flex flex-col">
                                        <label data-testid="ctg-patient-email-label" htmlFor="patient-email" className="block text-sm font-semibold text-gray-700 mb-2 h-6">
                                            {t('clicktogo.email_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <div className={`relative flex items-center border rounded-xl px-3 py-2.5 bg-white transition-all duration-300 h-[42px] ${patientErrors.email
                                            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
                                            : patientEmailStatus === false
                                                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                                : patientEmailStatus === true
                                                    ? 'border-green-300 focus-within:ring-2 focus-within:ring-green-500'
                                                    : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD] hover:border-[#4ABDBD]'
                                            }`}>
                                            <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                            <input
                                                data-testid="ctg-patient-email-input"
                                                id="patient-email"
                                                name="email"
                                                type="email"
                                                value={patientForm.email}
                                                onChange={handlePatientInputChange}
                                                placeholder={t('clicktogo.email_placeholder')}
                                                maxLength={254}
                                                className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm pr-8"
                                            />
                                            {emailCheckLoading && (
                                                <span data-testid="ctg-patient-email-checking-spinner" className="absolute right-3 w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                            )}
                                            {!emailCheckLoading && patientEmailStatus === true && (
                                                <Check data-testid="ctg-patient-email-available-icon" className="absolute right-3 text-green-500 w-5 h-5" />
                                            )}
                                            {!emailCheckLoading && patientEmailStatus === false && (
                                                <X data-testid="ctg-patient-email-taken-icon" className="absolute right-3 text-red-500 w-5 h-5" />
                                            )}
                                        </div>
                                        <div className="min-h-[20px] mt-1">
                                            {patientErrors.email && (
                                                <p data-testid="ctg-patient-email-error" className="text-red-500 text-xs">{patientErrors.email}</p>
                                            )}
                                            {!emailCheckLoading && patientEmailStatus === false && !patientErrors.email && (
                                                <p data-testid="ctg-patient-email-already-invited-msg" className="text-red-500 text-xs">{t('clicktogo.email_already_invited', 'Email already Invited. You can reinvite this user.')}</p>
                                            )}
                                        </div>
                                        {/* {patientEmailStatus === true && !patientErrors.email && (
                                            <p className="text-green-600 text-xs mt-1">Email is available</p>
                                        )} */}
                                    </div>

                                    {/* Send Button */}
                                    <div className="w-full sm:w-auto flex flex-col">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 h-6 opacity-0">
                                            {t('clicktogo.action_label')}
                                        </label>
                                        <button
                                            data-testid="ctg-patient-send-button"
                                            type="submit"
                                            disabled={isLoading}
                                            className={`flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto h-[42px] ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span data-testid="ctg-patient-send-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                                    {t('clicktogo.sending')}
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4" />
                                                    {t('clicktogo.send_button')}
                                                </>
                                            )}
                                        </button>
                                        <div className="min-h-[20px]"></div>
                                    </div>

                                    {/* Upload Excel Button */}
                                    <div className="w-full sm:w-auto flex flex-col">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 h-6 opacity-0">
                                            {t('clicktogo.action_label')}
                                        </label>
                                        <button
                                            data-testid="ctg-patient-upload-excel-button"
                                            type="button"
                                            onClick={handlePatientExcelClick}
                                            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto h-[42px]"
                                        >
                                            <FileSpreadsheet className="w-4 h-4" />
                                            {t('clicktogo.upload_excel')}
                                        </button>
                                        <div className="min-h-[20px]"></div>
                                    </div>
                                </div>
                            </form>

                            {/* Invitations Table */}
                            <div data-testid="ctg-patient-invitations-table-container" className="mt-6 overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="ctg-patient-invitations-table" className="w-full border-collapse rounded-md overflow-hidden">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                            <th data-testid="ctg-patient-table-header-sno" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.s_no')}</th>
                                            <th data-testid="ctg-patient-table-header-name" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.name')}</th>
                                            <th data-testid="ctg-patient-table-header-email" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.email')}</th>
                                            <th data-testid="ctg-patient-table-header-invited-by" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.invited_by')}</th>
                                            <th data-testid="ctg-patient-table-header-action" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('clicktogo.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoadingInvites ? (
                                            <tr data-testid="ctg-patient-invitations-loading-row">
                                                <td colSpan="5" className="p-4 text-center text-gray-500">
                                                    <span data-testid="ctg-patient-invitations-loading-spinner" className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                                    <span data-testid="ctg-patient-invitations-loading-text" className="ml-2">{t('clicktogo.loading')}</span>
                                                </td>
                                            </tr>
                                        ) : invitations && invitations.length > 0 ? (
                                            invitations.map((invitation, index) => {
                                                const serialNumber = ((currentPage || 1) - 1) * (itemsPerPage || 5) + index + 1;
                                                return (
                                                    <tr key={invitation._id} data-testid={`ctg-patient-invitation-row-${invitation._id}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                        <td data-testid={`ctg-patient-invitation-sno-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{serialNumber}</td>
                                                        <td data-testid={`ctg-patient-invitation-name-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.name || '-'}</td>
                                                        <td data-testid={`ctg-patient-invitation-email-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.email || '-'}</td>
                                                        <td data-testid={`ctg-patient-invitation-invited-by-${invitation._id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{invitation.adminName || '-'}</td>
                                                        <td className="p-3 text-xs text-center">
                                                            {invitation.isUsed ? (
                                                                <span data-testid={`ctg-patient-invitation-accepted-${invitation._id}`} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-green-500/10 to-green-300/10 text-green-700 border border-green-500/20">
                                                                    {t('clicktogo.accepted')}
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    data-testid={`ctg-patient-invitation-reinvite-button-${invitation._id}`}
                                                                    type="button"
                                                                    onClick={() => handleReinvite(invitation)}
                                                                    disabled={isReinviting}
                                                                    className={`px-4 py-1.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white text-xs font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer ${isReinviting ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                >
                                                                    {isReinviting ? t('clicktogo.sending_reinvite') : t('clicktogo.re_invite')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr data-testid="ctg-patient-invitations-empty-row">
                                                <td colSpan="5" className="p-4 text-center text-gray-500">
                                                    {t('clicktogo.no_invitations_found', 'No invitations found')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {!isLoadingInvites && total > 0 && (
                                <Pagination
                                    data-testid="ctg-patient-pagination"
                                    totalItems={total}
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    custombutton={invitations && invitations.length === 0 ? 'disable' : ''}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Doctor Excel Upload Modal */}
            {isDoctorModalOpen && (
                <div
                    data-testid="ctg-doctor-excel-modal"
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="doctor-excel-modal-title"
                >
                    <div data-testid="ctg-doctor-excel-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        <div data-testid="ctg-doctor-excel-modal-header" className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h3
                                data-testid="ctg-doctor-excel-modal-title"
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="doctor-excel-modal-title"
                            >
                                {t('clicktogo.excel_upload_doctor')}
                            </h3>
                            <button
                                data-testid="ctg-doctor-excel-modal-close"
                                onClick={handleCloseDoctorModal}
                                aria-label={t('clicktogo.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        <div data-testid="ctg-doctor-excel-modal-body" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4">
                            {/* File Input */}
                            <div>
                                <label data-testid="ctg-doctor-excel-file-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('clicktogo.select_excel_file')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="ctg-doctor-excel-file-input"
                                        id="doctor-excel-upload-input"
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={handleDoctorFileChange}
                                        className="hidden"
                                    />
                                    <label
                                        data-testid="ctg-doctor-excel-file-trigger"
                                        htmlFor="doctor-excel-upload-input"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span data-testid="ctg-doctor-excel-choose-file-button" className="px-4 py-2 rounded-xl bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('clicktogo.choose_file')}
                                        </span>
                                        <span data-testid="ctg-doctor-excel-file-name" className="text-sm text-gray-500 truncate">
                                            {doctorExcelFile ? doctorExcelFile.name : t('clicktogo.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                                {doctorFormatChecking && (
                                    <p data-testid="ctg-doctor-excel-format-checking" className="mt-2 text-xs text-gray-500 font-medium flex items-center gap-1">
                                        <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                        {t('clicktogo.checking_format', 'Checking file format...')}
                                    </p>
                                )}
                                {!doctorFormatChecking && doctorExcelFile && doctorFormatValid && !doctorFileError && (
                                    <p data-testid="ctg-doctor-excel-format-valid" className="mt-2 text-xs text-green-600 font-medium">
                                        {t('clicktogo.format_valid', 'File format looks good')}
                                    </p>
                                )}
                                {doctorFileError && (
                                    <p data-testid="ctg-doctor-excel-file-error" className="mt-2 text-xs text-red-500 font-medium">{doctorFileError}</p>
                                )}
                            </div>
                        </div>

                        <div data-testid="ctg-doctor-excel-modal-footer" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-between items-center gap-4">
                            <button
                                data-testid="ctg-doctor-excel-download-sample-button"
                                onClick={handleSampleExcelDownload}
                                disabled={isDownloadingExcel}
                                aria-label={t('clicktogo.download_sample_excel') || 'Download Sample Excel'}
                                className="group/btn flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow-lg hover:shadow-xl cursor-pointer transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {isDownloadingExcel ? (
                                    <>
                                        <span className="border-2 border-white border-t-transparent rounded-full w-3 h-3 sm:w-4 sm:h-4 animate-spin"></span>
                                        {t('clicktogo.downloading') || 'Downloading...'}
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                                        {t('clicktogo.download_sample') || 'Sample Excel'}
                                    </>
                                )}
                            </button>
                            <div className="flex gap-4">
                                <button
                                    data-testid="ctg-doctor-excel-cancel-button"
                                    type="button"
                                    onClick={handleCloseDoctorModal}
                                    className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                >
                                    {t('clicktogo.cancel')}
                                </button>
                                <button
                                    data-testid="ctg-doctor-excel-submit-button"
                                    type="button"
                                    onClick={handleDoctorExcelSubmit}
                                    disabled={!doctorExcelFile || !!doctorFileError || isUploading || doctorFormatChecking || !doctorFormatValid}
                                    className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${doctorExcelFile && !doctorFileError && !isUploading && doctorFormatValid && !doctorFormatChecking
                                        ? 'hover:scale-105 cursor-pointer'
                                        : 'opacity-50 cursor-not-allowed'
                                        }`}
                                >
                                    {isUploading ? (
                                        <>
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                            {t('clicktogo.uploading')}
                                        </>
                                    ) : (
                                        t('clicktogo.upload')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Patient Excel Upload Modal */}
            {isPatientModalOpen && (
                <div
                    data-testid="ctg-patient-excel-modal"
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="patient-excel-modal-title"
                >
                    <div data-testid="ctg-patient-excel-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        <div data-testid="ctg-patient-excel-modal-header" className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h3
                                data-testid="ctg-patient-excel-modal-title"
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="patient-excel-modal-title"
                            >
                                {t('clicktogo.excel_upload_patient')}
                            </h3>
                            <button
                                data-testid="ctg-patient-excel-modal-close"
                                onClick={handleClosePatientModal}
                                aria-label={t('clicktogo.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        <div data-testid="ctg-patient-excel-modal-body" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4">
                            {/* File Input */}
                            <div>
                                <label data-testid="ctg-patient-excel-file-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('clicktogo.select_excel_file')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="ctg-patient-excel-file-input"
                                        id="patient-excel-upload-input"
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={handlePatientFileChange}
                                        className="hidden"
                                    />
                                    <label
                                        data-testid="ctg-patient-excel-file-trigger"
                                        htmlFor="patient-excel-upload-input"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span data-testid="ctg-patient-excel-choose-file-button" className="px-4 py-2 rounded-xl bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('clicktogo.choose_file')}
                                        </span>
                                        <span data-testid="ctg-patient-excel-file-name" className="text-sm text-gray-500 truncate">
                                            {patientExcelFile ? patientExcelFile.name : t('clicktogo.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                                {patientFormatChecking && (
                                    <p data-testid="ctg-patient-excel-format-checking" className="mt-2 text-xs text-gray-500 font-medium flex items-center gap-1">
                                        <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                        {t('clicktogo.checking_format', 'Checking file format...')}
                                    </p>
                                )}
                                {!patientFormatChecking && patientExcelFile && patientFormatValid && !patientFileError && (
                                    <p data-testid="ctg-patient-excel-format-valid" className="mt-2 text-xs text-green-600 font-medium">
                                        {t('clicktogo.format_valid', 'File format looks good')}
                                    </p>
                                )}
                                {patientFileError && (
                                    <p data-testid="ctg-patient-excel-file-error" className="mt-2 text-xs text-red-500 font-medium">{patientFileError}</p>
                                )}
                            </div>
                        </div>

                        <div data-testid="ctg-patient-excel-modal-footer" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-between items-center gap-4">
                            <button
                                data-testid="ctg-patient-excel-download-sample-button"
                                onClick={handleSampleExcelDownload}
                                disabled={isDownloadingExcel}
                                aria-label={t('clicktogo.download_sample_excel') || 'Download Sample Excel'}
                                className="group/btn flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow-lg hover:shadow-xl cursor-pointer transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {isDownloadingExcel ? (
                                    <>
                                        <span className="border-2 border-white border-t-transparent rounded-full w-3 h-3 sm:w-4 sm:h-4 animate-spin"></span>
                                        {t('clicktogo.downloading') || 'Downloading...'}
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                                        {t('clicktogo.download_sample') || 'Sample Excel'}
                                    </>
                                )}
                            </button>
                            <div className="flex gap-4">
                                <button
                                    data-testid="ctg-patient-excel-cancel-button"
                                    type="button"
                                    onClick={handleClosePatientModal}
                                    className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                >
                                    {t('clicktogo.cancel')}
                                </button>
                                <button
                                    data-testid="ctg-patient-excel-submit-button"
                                    type="button"
                                    onClick={handlePatientExcelSubmit}
                                    disabled={!patientExcelFile || !!patientFileError || isUploading || patientFormatChecking || !patientFormatValid}
                                    className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${patientExcelFile && !patientFileError && !isUploading && patientFormatValid && !patientFormatChecking
                                        ? 'hover:scale-105 cursor-pointer'
                                        : 'opacity-50 cursor-not-allowed'
                                        }`}
                                >
                                    {isUploading ? (
                                        <>
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                            {t('clicktogo.uploading')}
                                        </>
                                    ) : (
                                        t('clicktogo.upload')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Excel Upload Result Modal */}
            {excelResultModal.isOpen && (
                <div
                    data-testid="ctg-excel-result-modal"
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
                    role="dialog"
                    aria-labelledby="excel-result-modal-title"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeExcelResultModal();
                    }}
                >
                    <div data-testid="ctg-excel-result-modal-content" className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fade-in">
                        {/* Header */}
                        <div data-testid="ctg-excel-result-modal-header" className="p-6 flex items-center justify-between border-b border-gray-200 rounded-t-2xl">
                            <div className="flex items-center gap-4">
                                {/* Icon */}
                                <div data-testid={`ctg-excel-result-modal-icon-${excelResultModal.type}`} className={`w-16 h-16 rounded-full flex items-center justify-center ${excelResultModal.type === 'success' ? 'bg-green-100' :
                                    excelResultModal.type === 'warning' ? 'bg-yellow-100' :
                                        excelResultModal.type === 'error' ? 'bg-red-100' :
                                            'bg-blue-100'
                                    }`}>
                                    {excelResultModal.type === 'success' && (
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                    {excelResultModal.type === 'warning' && (
                                        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    )}
                                    {excelResultModal.type === 'error' && (
                                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                    {excelResultModal.type === 'info' && (
                                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                </div>
                                <h3
                                    data-testid="ctg-excel-result-modal-title"
                                    id="excel-result-modal-title"
                                    className="text-2xl font-bold text-gray-800"
                                >
                                    {excelResultModal.title}
                                </h3>
                            </div>
                            <button
                                data-testid="ctg-excel-result-modal-close"
                                onClick={closeExcelResultModal}
                                aria-label={t('clicktogo.close_modal')}
                                className="text-gray-500 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition-all duration-300"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div data-testid="ctg-excel-result-modal-body" className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
                            {/* Message */}
                            {excelResultModal.message && (
                                <div data-testid="ctg-excel-result-modal-message" className="mb-4 text-gray-700 whitespace-pre-line">
                                    {excelResultModal.message}
                                </div>
                            )}

                            {/* User Exists Section */}
                            {excelResultModal.userExistsDetails.length > 0 && (
                                <div data-testid="ctg-excel-result-user-exists-section" className="mb-6">
                                    <h4 data-testid="ctg-excel-result-user-exists-heading" className="text-lg font-semibold text-gray-800 mb-3">{t('clicktogo.users_already_exist', 'Users Already Exist')}</h4>
                                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                                        <table data-testid="ctg-excel-result-user-exists-table" className="w-full border-collapse">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th data-testid="ctg-excel-result-user-exists-header-sno" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.s_no')}</th>
                                                    <th data-testid="ctg-excel-result-user-exists-header-name" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.name')}</th>
                                                    <th data-testid="ctg-excel-result-user-exists-header-email" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.email')}</th>
                                                    <th data-testid="ctg-excel-result-user-exists-header-status" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelResultModal.userExistsDetails.map((detail, index) => (
                                                    <tr key={index} data-testid={`ctg-excel-result-user-exists-row-${index}`} className="border-b border-gray-200 hover:bg-white">
                                                        <td data-testid={`ctg-excel-result-user-exists-sno-${index}`} className="p-3 text-sm text-gray-600">{index + 1}</td>
                                                        <td data-testid={`ctg-excel-result-user-exists-name-${index}`} className="p-3 text-sm text-gray-600">{detail.name || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-user-exists-email-${index}`} className="p-3 text-sm text-gray-600 break-all">{detail.email || '-'}</td>
                                                        <td className="p-3 text-sm">
                                                            <span data-testid={`ctg-excel-result-user-exists-status-${index}`} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                                                                {detail.status || t('clicktogo.user_already_exists_status', 'User already exists')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Duplicate Invitations Section */}
                            {excelResultModal.duplicateDetails.length > 0 && (
                                <div data-testid="ctg-excel-result-duplicate-section" className="mb-6">
                                    <h4 data-testid="ctg-excel-result-duplicate-heading" className="text-lg font-semibold text-gray-800 mb-3">{t('clicktogo.duplicate_invitations', 'Duplicate Invitations')}</h4>
                                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                                        <table data-testid="ctg-excel-result-duplicate-table" className="w-full border-collapse">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th data-testid="ctg-excel-result-duplicate-header-sno" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.s_no')}</th>
                                                    <th data-testid="ctg-excel-result-duplicate-header-name" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.name')}</th>
                                                    <th data-testid="ctg-excel-result-duplicate-header-email" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.email')}</th>
                                                    <th data-testid="ctg-excel-result-duplicate-header-status" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelResultModal.duplicateDetails.map((detail, index) => (
                                                    <tr key={index} data-testid={`ctg-excel-result-duplicate-row-${index}`} className="border-b border-gray-200 hover:bg-white">
                                                        <td data-testid={`ctg-excel-result-duplicate-sno-${index}`} className="p-3 text-sm text-gray-600">{index + 1}</td>
                                                        <td data-testid={`ctg-excel-result-duplicate-name-${index}`} className="p-3 text-sm text-gray-600">{detail.name || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-duplicate-email-${index}`} className="p-3 text-sm text-gray-600 break-all">{detail.email || '-'}</td>
                                                        <td className="p-3 text-sm">
                                                            <span data-testid={`ctg-excel-result-duplicate-status-${index}`} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                                                {detail.status || t('clicktogo.invitation_already_exists_status', 'Invitation already exists')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Sent Invitations Section */}
                            {excelResultModal.details.filter(d => (d.status || '').toLowerCase() === 'sent').length > 0 && (
                                <div data-testid="ctg-excel-result-sent-section" className="mb-6">
                                    <h4 data-testid="ctg-excel-result-sent-heading" className="text-lg font-semibold text-gray-800 mb-3">{t('clicktogo.sent_invitations', 'Sent Invitations')}</h4>
                                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                                        <table data-testid="ctg-excel-result-sent-table" className="w-full border-collapse">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th data-testid="ctg-excel-result-sent-header-sno" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.s_no')}</th>
                                                    <th data-testid="ctg-excel-result-sent-header-name" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.name')}</th>
                                                    <th data-testid="ctg-excel-result-sent-header-email" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.email')}</th>
                                                    <th data-testid="ctg-excel-result-sent-header-role" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.role', 'Role')}</th>
                                                    <th data-testid="ctg-excel-result-sent-header-status" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelResultModal.details.filter(d => (d.status || '').toLowerCase() === 'sent').map((detail, index) => (
                                                    <tr key={index} data-testid={`ctg-excel-result-sent-row-${index}`} className="border-b border-gray-200 hover:bg-white">
                                                        <td data-testid={`ctg-excel-result-sent-sno-${index}`} className="p-3 text-sm text-gray-600">{index + 1}</td>
                                                        <td data-testid={`ctg-excel-result-sent-name-${index}`} className="p-3 text-sm text-gray-600">{detail.name || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-sent-email-${index}`} className="p-3 text-sm text-gray-600 break-all">{detail.email || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-sent-role-${index}`} className="p-3 text-sm text-gray-600">{detail.role || '-'}</td>
                                                        <td className="p-3 text-sm">
                                                            <span data-testid={`ctg-excel-result-sent-status-${index}`} className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                {detail.status || '-'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Failed Invitations Section */}
                            {excelResultModal.details.filter(d => (d.status || '').toLowerCase() === 'failed').length > 0 && (
                                <div data-testid="ctg-excel-result-failed-section" className="mb-6">
                                    <h4 data-testid="ctg-excel-result-failed-heading" className="text-lg font-semibold text-gray-800 mb-3">{t('clicktogo.failed_invitations', 'Failed Invitations')}</h4>
                                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                                        <table data-testid="ctg-excel-result-failed-table" className="w-full border-collapse">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th data-testid="ctg-excel-result-failed-header-sno" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.s_no')}</th>
                                                    <th data-testid="ctg-excel-result-failed-header-name" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.name')}</th>
                                                    <th data-testid="ctg-excel-result-failed-header-email" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.email')}</th>
                                                    <th data-testid="ctg-excel-result-failed-header-role" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.role', 'Role')}</th>
                                                    <th data-testid="ctg-excel-result-failed-header-status" className="p-3 text-left text-sm font-semibold text-gray-700 border-b">{t('clicktogo.status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelResultModal.details.filter(d => (d.status || '').toLowerCase() === 'failed').map((detail, index) => (
                                                    <tr key={index} data-testid={`ctg-excel-result-failed-row-${index}`} className="border-b border-gray-200 hover:bg-white">
                                                        <td data-testid={`ctg-excel-result-failed-sno-${index}`} className="p-3 text-sm text-gray-600">{index + 1}</td>
                                                        <td data-testid={`ctg-excel-result-failed-name-${index}`} className="p-3 text-sm text-gray-600">{detail.name || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-failed-email-${index}`} className="p-3 text-sm text-gray-600 break-all">{detail.email || '-'}</td>
                                                        <td data-testid={`ctg-excel-result-failed-role-${index}`} className="p-3 text-sm text-gray-600">{detail.role || '-'}</td>
                                                        <td className="p-3 text-sm">
                                                            <span data-testid={`ctg-excel-result-failed-status-${index}`} className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                {detail.status || '-'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Summary Section */}
                            {excelResultModal.summary && (
                                <div data-testid="ctg-excel-result-summary-section" className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-[#4ABDBD]">
                                    <h4 data-testid="ctg-excel-result-summary-heading" className="text-lg font-semibold text-gray-800 mb-3">{t('clicktogo.summary')}</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {excelResultModal.summary.successful > 0 && (
                                            <div data-testid="ctg-excel-result-summary-successful" className="bg-white p-3 rounded-lg shadow-sm">
                                                <div data-testid="ctg-excel-result-summary-successful-label" className="text-sm text-gray-600">{t('clicktogo.successful')}</div>
                                                <div data-testid="ctg-excel-result-summary-successful-value" className="text-2xl font-bold text-green-600">{excelResultModal.summary.successful}</div>
                                            </div>
                                        )}
                                        {excelResultModal.summary.failed > 0 && (
                                            <div data-testid="ctg-excel-result-summary-failed" className="bg-white p-3 rounded-lg shadow-sm">
                                                <div data-testid="ctg-excel-result-summary-failed-label" className="text-sm text-gray-600">{t('clicktogo.failed')}</div>
                                                <div data-testid="ctg-excel-result-summary-failed-value" className="text-2xl font-bold text-red-600">{excelResultModal.summary.failed}</div>
                                            </div>
                                        )}
                                        {excelResultModal.summary.duplicate > 0 && (
                                            <div data-testid="ctg-excel-result-summary-duplicate" className="bg-white p-3 rounded-lg shadow-sm">
                                                <div data-testid="ctg-excel-result-summary-duplicate-label" className="text-sm text-gray-600">{t('clicktogo.duplicates', 'Duplicates')}</div>
                                                <div data-testid="ctg-excel-result-summary-duplicate-value" className="text-2xl font-bold text-yellow-600">{excelResultModal.summary.duplicate}</div>
                                            </div>
                                        )}
                                        {excelResultModal.summary.userExists > 0 && (
                                            <div data-testid="ctg-excel-result-summary-user-exists" className="bg-white p-3 rounded-lg shadow-sm">
                                                <div data-testid="ctg-excel-result-summary-user-exists-label" className="text-sm text-gray-600">{t('clicktogo.users_exist', 'Users Exist')}</div>
                                                <div data-testid="ctg-excel-result-summary-user-exists-value" className="text-2xl font-bold text-orange-600">{excelResultModal.summary.userExists}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Reinvite Question */}
                            {excelResultModal.showReinvite && excelResultModal.duplicateDetails.length > 0 && (
                                <div data-testid="ctg-excel-result-reinvite-question" className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-gray-700 font-medium">
                                        {t('clicktogo.reinvite_question', 'These emails already have invitations. Do you want to reinvite them?')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer Buttons */}
                        <div data-testid="ctg-excel-result-modal-footer" className="p-6 border-t border-gray-200 flex justify-between items-center gap-3 rounded-b-2xl">
                            {/* Download Excel Button - Left Side */}
                            <button
                                data-testid="ctg-excel-result-download-button"
                                onClick={handleDownloadModalExcel}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                {t('clicktogo.download_excel') || 'Download Excel'}
                            </button>

                            {/* Right Side Buttons */}
                            <div className="flex gap-3">
                                {excelResultModal.onCancel && (
                                    <button
                                        data-testid="ctg-excel-result-cancel-button"
                                        onClick={() => {
                                            if (excelResultModal.onCancel) excelResultModal.onCancel();
                                            closeExcelResultModal();
                                        }}
                                        className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                                    >
                                        {excelResultModal.showReinvite ? t('clicktogo.cancel') : t('clicktogo.close', 'Close')}
                                    </button>
                                )}
                                {excelResultModal.onConfirm && (
                                    <button
                                        data-testid="ctg-excel-result-confirm-button"
                                        onClick={() => {
                                            if (excelResultModal.onConfirm) excelResultModal.onConfirm();
                                            if (!excelResultModal.showReinvite) closeExcelResultModal();
                                        }}
                                        className="px-6 py-2.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] text-white rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                                    >
                                        {excelResultModal.showReinvite ? t('clicktogo.yes_re_invite') : t('common.ok', { ns: 'common' })}
                                    </button>
                                )}
                                {!excelResultModal.onConfirm && !excelResultModal.onCancel && (
                                    <button
                                        data-testid="ctg-excel-result-close-button"
                                        onClick={closeExcelResultModal}
                                        className="px-6 py-2.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] text-white rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                                    >
                                        {t('clicktogo.close', 'Close')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClickToGo;

// Failed to load resource: the server responded with a status of 404 (Not Found)Understand this error
// /api/custom-price/patient/DOC00034/doctor/DOC00034/speciality/SP00001/appointment-type/AT00018:1  Failed to load resource: the server responded with a status of 404 (Not Found)Understand this error
// AppointmentModal.jsx:1872 Uncaught TypeError: z.find is not a function
//     at AppointmentModal.jsx:1872:86
//     at ed (AppointmentModal.jsx:1876:17)
//     at T2 (vendor-9RnD4LoQ.js:sourcemap:48:48810)
//     at q2 (vendor-9RnD4LoQ.js:sourcemap:48:71721)
//     at hB (vendor-9RnD4LoQ.js:sourcemap:48:82142)
//     at VB (vendor-9RnD4LoQ.js:sourcemap:48:118070)
//     at Cce (vendor-9RnD4LoQ.js:sourcemap:48:117104)
//     at hT (vendor-9RnD4LoQ.js:sourcemap:48:116932)
//     at FB (vendor-9RnD4LoQ.js:sourcemap:48:113694)
//     at r6 (vendor-9RnD4LoQ.js:sourcemap:48:125748)Understand this error
// SupportSocketContext.jsx:131 🧹 SupportSocketProvider: Cleaning up